import { Injectable } from '@angular/core';
import { HttpHeaders, HttpRequest } from '@angular/common/http';
import { openDB, DBSchema } from 'idb';

export interface QueuedRequest {
  url: string;
  method: string;
  body: unknown;
  headers: Record<string, string>;
  status?: 'pending' | 'sent' | 'failed';
}

export interface StoredRequest extends QueuedRequest {
  id: number;
}

interface OfflineDB extends DBSchema {
  requests: {
    key: number;
    value: Omit<StoredRequest, 'id'>;
  };
}

@Injectable({ providedIn: 'root' })
export class OfflineQueueService {
  private dbPromise = openDB<OfflineDB>('offline-db', 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('requests')) {
        db.createObjectStore('requests', {
          keyPath: 'id',
          autoIncrement: true,
        });
      }
    },
  });

  async addRequest(req: HttpRequest<unknown>): Promise<void> {
    let relativeUrl: string;

    try {
      const parsed = new URL(req.url);
      relativeUrl = parsed.pathname.replace(/^\/api\/v1/, '').replace(/^\/+/, '');
      if (parsed.search) relativeUrl += parsed.search;
    } catch {
      relativeUrl = req.url.replace(/^\/+/, '');
    }

    console.log('📥 Stored relative URL:', relativeUrl);

    const queuedRequest: QueuedRequest = {
      url: relativeUrl,
      method: req.method,
      body: this.cloneBody(req.body),
      headers: req.headers.keys().reduce<Record<string, string>>((acc, key) => {
        acc[key] = req.headers.get(key) ?? '';
        return acc;
      }, {}),
      status: 'pending',
    };

    const db = await this.dbPromise;
    await db.add('requests', queuedRequest as Omit<StoredRequest, 'id'>);
  }

  rebuildHttpRequest(qr: StoredRequest): HttpRequest<unknown> {
    const fullUrl = qr.url.startsWith('http')
      ? qr.url
      : `${qr.url.replace(/^\/+/, '')}`;

    console.log('🚀 Rebuilt full URL:', fullUrl);

    return new HttpRequest(qr.method, fullUrl, qr.body, {
      headers: new HttpHeaders(qr.headers),
    });
  }

  async getRequests(): Promise<StoredRequest[]> {
    const db = await this.dbPromise;
    return (await db.getAll('requests')) as StoredRequest[];
  }

  async removeRequest(id: number): Promise<void> {
    const db = await this.dbPromise;
    await db.delete('requests', id);
  }

  private cloneBody(body: unknown): unknown {
    if (body instanceof FormData) {
      const obj: Record<string, unknown> = {};
      body.forEach((value, key) => {
        obj[key] = value;
      });
      return obj;
    }
    return body;
  }
}
